import React, { useState } from 'react';
import Icon from 'components/AppIcon';
import Button from 'components/ui/Button';

export default function HoldingsTable({ holdings, onAddStock }) {
  const [sortField, setSortField] = useState('ticker');
  const [sortDir, setSortDir] = useState('asc');
  const [expandedRow, setExpandedRow] = useState(null);

  const handleSort = (field) => {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('asc'); }
  };

  const sorted = [...holdings]?.sort((a, b) => {
    let av = a?.[sortField], bv = b?.[sortField];
    if (typeof av === 'string') av = av?.toLowerCase(), bv = bv?.toLowerCase();
    return sortDir === 'asc' ? (av > bv ? 1 : -1) : (av < bv ? 1 : -1);
  });

  const SortIcon = ({ field }) => (
    <span className="ml-1 inline-flex flex-col" style={{ lineHeight: 0 }}>
      <Icon name={sortField === field && sortDir === 'asc' ? 'ChevronUp' : 'ChevronsUpDown'} size={12} color={sortField === field ? 'var(--color-accent)' : 'currentColor'} />
    </span>
  );

  const ColHeader = ({ label, field, className = '' }) => (
    <th className={`px-3 py-3 text-left font-caption text-xs font-semibold uppercase tracking-wider cursor-pointer select-none whitespace-nowrap ${className}`}
      style={{ color: 'var(--color-muted-foreground)' }}
      onClick={() => handleSort(field)}>
      {label}<SortIcon field={field} />
    </th>
  );

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-heading font-semibold text-lg md:text-xl" style={{ color: 'var(--color-foreground)' }}>Holdings</h2>
        <Button variant="default" size="sm" iconName="Plus" iconPosition="left" onClick={onAddStock}>Add Stock</Button>
      </div>
      {/* Desktop Table */}
      <div className="hidden md:block overflow-x-auto rounded-xl border" style={{ borderColor: 'var(--color-border)' }}>
        <table className="w-full min-w-[700px]">
          <thead style={{ background: 'var(--color-muted)' }}>
            <tr>
              <ColHeader label="Ticker" field="ticker" />
              <ColHeader label="Company" field="name" />
              <ColHeader label="Shares" field="shares" />
              <ColHeader label="Avg Buy" field="avgBuy" />
              <ColHeader label="Current" field="currentPrice" />
              <ColHeader label="Change" field="changePercent" />
              <ColHeader label="Gain/Loss" field="gainLoss" />
              <th className="px-3 py-3 text-left font-caption text-xs font-semibold uppercase tracking-wider" style={{ color: 'var(--color-muted-foreground)' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {sorted?.map((h, i) => {
              const isPos = h?.changePercent >= 0;
              return (
                <tr key={h?.ticker} className="border-t transition-colors hover:bg-opacity-50"
                  style={{ borderColor: 'var(--color-border)', background: i % 2 === 0 ? 'var(--color-background)' : 'var(--color-surface)' }}>
                  <td className="px-3 py-3">
                    <span className="font-data font-bold text-sm" style={{ color: 'var(--color-primary)' }}>{h?.ticker}</span>
                  </td>
                  <td className="px-3 py-3">
                    <div>
                      <p className="font-caption font-medium text-sm line-clamp-1" style={{ color: 'var(--color-foreground)' }}>{h?.name}</p>
                      <p className="font-caption text-xs" style={{ color: 'var(--color-muted-foreground)' }}>{h?.sector}</p>
                    </div>
                  </td>
                  <td className="px-3 py-3 font-data text-sm whitespace-nowrap" style={{ color: 'var(--color-foreground)' }}>{h?.shares}</td>
                  <td className="px-3 py-3 font-data text-sm whitespace-nowrap" style={{ color: 'var(--color-foreground)' }}>${h?.avgBuy?.toFixed(2)}</td>
                  <td className="px-3 py-3 font-data text-sm whitespace-nowrap" style={{ color: 'var(--color-foreground)' }}>${h?.currentPrice?.toFixed(2)}</td>
                  <td className="px-3 py-3 whitespace-nowrap">
                    <span className={`flex items-center gap-1 font-data text-sm font-semibold ${isPos ? 'text-emerald-600' : 'text-red-600'}`}>
                      <Icon name={isPos ? 'ArrowUpRight' : 'ArrowDownRight'} size={14} color="currentColor" />
                      {isPos ? '+' : ''}{h?.changePercent?.toFixed(2)}%
                    </span>
                  </td>
                  <td className="px-3 py-3 whitespace-nowrap">
                    <span className={`font-data text-sm font-semibold ${h?.gainLoss >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                      {h?.gainLoss >= 0 ? '+' : ''}${h?.gainLoss?.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                    </span>
                  </td>
                  <td className="px-3 py-3">
                    <div className="flex items-center gap-1">
                      <button className="p-1.5 rounded-md hover:bg-amber-50 transition-colors" title="Add to watchlist">
                        <Icon name="Star" size={14} color="var(--color-accent)" />
                      </button>
                      <button className="p-1.5 rounded-md hover:bg-blue-50 transition-colors" title="View details">
                        <Icon name="ExternalLink" size={14} color="var(--color-primary)" />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {/* Mobile Cards */}
      <div className="md:hidden space-y-3">
        {sorted?.map((h) => {
          const isPos = h?.changePercent >= 0;
          const isExpanded = expandedRow === h?.ticker;
          return (
            <div key={h?.ticker} className="rounded-xl border overflow-hidden"
              style={{ borderColor: 'var(--color-border)', background: 'var(--color-card)' }}>
              <button className="w-full flex items-center justify-between p-4" onClick={() => setExpandedRow(isExpanded ? null : h?.ticker)}>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                    style={{ background: 'var(--color-primary)' }}>
                    <span className="font-data font-bold text-xs text-white">{h?.ticker?.slice(0, 3)}</span>
                  </div>
                  <div className="text-left">
                    <p className="font-data font-bold text-sm" style={{ color: 'var(--color-primary)' }}>{h?.ticker}</p>
                    <p className="font-caption text-xs line-clamp-1" style={{ color: 'var(--color-muted-foreground)' }}>{h?.name}</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className={`font-data font-semibold text-sm ${isPos ? 'text-emerald-600' : 'text-red-600'}`}>
                    {isPos ? '+' : ''}{h?.changePercent?.toFixed(2)}%
                  </p>
                  <p className="font-data text-xs" style={{ color: 'var(--color-foreground)' }}>${h?.currentPrice?.toFixed(2)}</p>
                </div>
              </button>
              {isExpanded && (
                <div className="px-4 pb-4 grid grid-cols-2 gap-3 border-t" style={{ borderColor: 'var(--color-border)' }}>
                  {[
                    { label: 'Shares', value: h?.shares },
                    { label: 'Avg Buy', value: `$${h?.avgBuy?.toFixed(2)}` },
                    { label: 'Gain/Loss', value: `${h?.gainLoss >= 0 ? '+' : ''}$${h?.gainLoss?.toFixed(2)}`, color: h?.gainLoss >= 0 ? 'text-emerald-600' : 'text-red-600' },
                    { label: 'Sector', value: h?.sector },
                  ]?.map(item => (
                    <div key={item?.label} className="pt-3">
                      <p className="font-caption text-xs" style={{ color: 'var(--color-muted-foreground)' }}>{item?.label}</p>
                      <p className={`font-data text-sm font-medium mt-0.5 ${item?.color || ''}`} style={!item?.color ? { color: 'var(--color-foreground)' } : {}}>{item?.value}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}